// User Documents API functions
import { getScribeHttpClients } from '@heidi/api/client';
import type {
  DocumentListResponse,
  DocumentStatusResponse,
  DocumentsAndJobsResponse,
  DownloadUrlResponse,
  JobResponse,
  UserDocumentItem,
  UserDocumentKind,
  UserDocumentScope,
} from '../../../contracts';

// Resolved per call, never at module scope: the registry is app-wired at
// bootstrap and throws until it is. Same `scribeHttpV2` singleton, so the
// interceptor stack — bearer, workspace header, temp-user id — is unchanged.
const http = () => getScribeHttpClients().scribeHttpV2;

const BASE_PATH = '/evidence/user-documents';

/**
 * Upload a document for processing and embedding.
 * Returns a JobResponse - the actual document is created when the job completes.
 *
 * @param file - The file to upload
 * @param scope - Access scope: 'user' (personal) or 'team' (team-shared)
 * @param onProgress - Optional callback for upload progress
 * @param collectionId - Optional collection ID to auto-add document to after processing
 * @param clientUploadId - Optional client UUID for idempotent uploads
 * @param metadata - Optional JSON metadata (e.g. `{ document_kind: 'workflow_context' }`)
 */
export async function uploadUserDocument(
  file: File,
  scope: UserDocumentScope = 'user',
  onProgress?: (progress: number) => void,
  collectionId?: string | null,
  clientUploadId?: string | null,
  metadata?: { document_kind?: UserDocumentKind } | null
): Promise<JobResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('scope', scope);
  if (collectionId) {
    formData.append('collection_id', collectionId);
  }
  if (clientUploadId) {
    formData.append('client_upload_id', clientUploadId);
  }
  if (metadata) {
    formData.append('metadata', JSON.stringify(metadata));
  }

  const response = await http().post<JobResponse>(BASE_PATH, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  return response.data;
}

/**
 * Get the processing status of a job
 */
export async function getJobStatus(
  jobId: string,
  options?: { signal?: AbortSignal; timeout?: number }
) {
  const path = `${BASE_PATH}/jobs/${jobId}/status`;
  const response = await (options
    ? http().get<DocumentStatusResponse>(path, options)
    : http().get<DocumentStatusResponse>(path));
  return response.data;
}

/**
 * @deprecated Use getJobStatus instead - documents don't have status, jobs do
 */
export async function getDocumentStatus(documentId: string) {
  return getJobStatus(documentId);
}

/**
 * List all documents accessible to the current user (completed only)
 */
export async function listUserDocuments(
  scope: 'all' | 'user' | 'team' = 'all',
  documentKind?: 'input' | 'artifact' | 'workflow_context'
) {
  const response = await http().get<DocumentListResponse>(BASE_PATH, {
    params: {
      scope,
      ...(documentKind ? { document_kind: documentKind } : {}),
    },
  });
  return response.data;
}

/**
 * List both active jobs and completed documents.
 * This is the preferred method for the Library UI as it shows the full picture.
 */
export async function listDocumentsAndJobs(): Promise<DocumentsAndJobsResponse> {
  const response = await http().get<DocumentsAndJobsResponse>(`${BASE_PATH}/all`);
  return response.data;
}

/**
 * Delete a document and all its embeddings
 */
export async function deleteUserDocument(documentId: string) {
  await http().delete(`${BASE_PATH}/${documentId}`);
}

/**
 * Delete a job and any partial data
 */
export async function deleteJob(jobId: string) {
  await http().delete(`${BASE_PATH}/jobs/${jobId}`);
}

/**
 * Update a document's scope (visibility).
 * Changes whether the document is personal (user-only) or team-shared.
 * @param documentId - The ID of the document to update
 * @param scope - The new scope: 'user' (personal) or 'team' (team-shared)
 * @returns The updated document
 */
export async function updateUserDocumentScope(
  documentId: string,
  scope: UserDocumentScope
): Promise<UserDocumentItem> {
  const response = await http().patch<UserDocumentItem>(`${BASE_PATH}/${documentId}`, {
    scope,
  });
  return response.data;
}

/**
 * Rename a document (update its display filename).
 * @param documentId - The ID of the document to rename
 * @param filename - The new filename
 * @returns The updated document
 */
export async function renameUserDocument(
  documentId: string,
  filename: string
): Promise<UserDocumentItem> {
  const response = await http().patch<UserDocumentItem>(`${BASE_PATH}/${documentId}`, {
    filename,
  });
  return response.data;
}

/**
 * Retry a failed job.
 * Only works if the job is in FAILED status and has an S3 file.
 */
export async function retryJob(jobId: string): Promise<JobResponse> {
  const response = await http().post<JobResponse>(`${BASE_PATH}/jobs/${jobId}/retry`);
  return response.data;
}

/**
 * Get a pre-signed download URL for a document.
 * The URL is valid for 60 minutes.
 * @param documentId - The ID of the document to get a download URL for
 * @returns A pre-signed URL for downloading/viewing the document
 */
export async function getDocumentDownloadUrl(
  documentId: string
): Promise<DownloadUrlResponse> {
  const response = await http().get<DownloadUrlResponse>(
    `${BASE_PATH}/${documentId}/download-url`
  );
  return response.data;
}

/**
 * FDE-8: Get an *inline* preview URL for a document. The backend returns a
 * presigned S3 URL with `Content-Disposition: inline` so browsers render the
 * file (e.g. native PDF viewer) instead of downloading it. Used by the
 * library-grounded chip source drawer. Callers can append `#page=N` to the
 * returned `download_url` to deep-link to a specific PDF page.
 */
export async function getDocumentPreviewUrl(
  documentId: string
): Promise<DownloadUrlResponse> {
  const response = await http().get<DownloadUrlResponse>(
    `${BASE_PATH}/${documentId}/preview-url`
  );
  return response.data;
}

/**
 * Share a user document by email.
 * @param documentId - The ID of the document to share
 * @param toEmail - The recipient email address
 */
export async function shareUserDocumentByEmail(
  documentId: string,
  toEmail: string
): Promise<void> {
  await http().post(`${BASE_PATH}/${documentId}:share-email`, {
    to_email: toEmail,
  });
}
